// Shaded "Failure: <code> - <text>" bands over the Day (Hourly) history
// chart for every stretch of minutes the turbine reported a non-zero error
// code (2026-10-24 request, modelled on a reference SCADA screenshot) -
// shown whatever two series are plotted, so a stop is visible without first
// picking "Error" in a dropdown.

// HistoryData snapshots are one per minute (Orbital-server mqttIngest.js
// persistHistorySnapshots) and only when telemetry arrived that minute, so
// a gap longer than this means the turbine wasn't reporting - the band is
// split there instead of claiming the error lasted through the outage.
const MAX_GAP_MS = 5 * 60 * 1000;
const SNAPSHOT_MS = 60 * 1000;

// Schemas with no error-code register at all report faults through their
// State register instead - which State codes are faults is per schema.
// RAGO 60kW (2026-10-25, confirmed by the user): 0 = Emergency STOP,
// 1-19 normal operation, 20 and up errors/defects/alarms.
const FAULT_STATES_BY_SCHEMA = {
  rago60kw: (state) => state === 0 || state >= 20,
};

const toCode = (raw) => {
  const code = Math.round(parseFloat(raw));
  return Number.isFinite(code) ? code : null;
};

const errorCodeOf = (values, isFaultState) => {
  if (!values) return null;
  if (values.ERROR !== undefined || values.ERR !== undefined) {
    return toCode(values.ERROR !== undefined ? values.ERROR : values.ERR);
  }
  if (isFaultState && values.STATE !== undefined) {
    const state = toCode(values.STATE);
    return state === null ? null : { state, fault: isFaultState(state) };
  }
  return null;
};

const hasErrorRegister = (params) =>
  (params || []).some(
    (p) => p.dbname && ["err", "error"].includes(p.dbname.toLowerCase())
  );

// History's series dropdown for a schema with no error register (RAGO)
// gets an "Error" series anyway (2026-10-25 request), derived from State by
// historyValue() below - only here, not in getAllMonitoringParameters
// itself, because the live graph has no such value to plot. details = the
// State texts, which DataGraph's tooltip shows next to the code.
export const withDerivedErrorParam = (params, schemaId, states) => {
  if (!FAULT_STATES_BY_SCHEMA[schemaId] || hasErrorRegister(params)) {
    return params;
  }
  return [
    ...params,
    {
      dbname: "err",
      name: "Error",
      unit: "",
      scale: 1,
      // 0 on this series means "no fault", not State 0 (Emergency STOP) -
      // that one is only labelled by its error band.
      details: (states || [])
        .filter((s) => s.index !== 0)
        .map((s) => ({ number: s.index, text: s.text })),
    },
  ];
};

// A history snapshot's value for `key` (uppercase), with the derived ERR of
// withDerivedErrorParam: the State code while it is a fault, 0 otherwise.
export const historyValue = (values, key, schemaId) => {
  if (!values) return NaN;
  if (values[key] !== undefined) return parseFloat(values[key]);
  const isFaultState = FAULT_STATES_BY_SCHEMA[schemaId];
  if ((key === "ERR" || key === "ERROR") && isFaultState) {
    const state = toCode(values.STATE);
    if (state === null) return NaN;
    return isFaultState(state) ? state : 0;
  }
  return NaN;
};

// rows: [{timestamp, values}] as DataGraph gets them; failures/states are
// turbineParams.Failure/.State. Returns [{start, end, code, text}] in ms,
// one per contiguous run of the same code.
export const findErrorIntervals = (rows, { failures, states, schemaId } = {}) => {
  const isFaultState = FAULT_STATES_BY_SCHEMA[schemaId];
  const textOf = (code, fromState) => {
    const entry = fromState
      ? (states || []).find((s) => s.index === code)
      : (failures || []).find((f) => f.number === code);
    return entry ? entry.text : null;
  };

  // Each point: its code, whether that code is a fault, and whether it came
  // from State (Emergency STOP is State 0 - a fault whose code is 0 - so
  // "fault" can't just be code !== 0).
  const points = (rows || [])
    .filter((r) => r && r.timestamp)
    .map((r) => {
      const t = Math.floor(r.timestamp);
      const c = errorCodeOf(r.values, isFaultState);
      if (c !== null && typeof c === "object") {
        return { t, code: c.state, fault: c.fault, fromState: true };
      }
      return { t, code: c, fault: c !== 0, fromState: false };
    })
    .filter((p) => p.code !== null)
    .sort((a, b) => a.t - b.t);

  const intervals = [];
  let current = null;
  points.forEach((p) => {
    const continues =
      current &&
      p.fault &&
      p.code === current.code &&
      p.fromState === current.fromState &&
      p.t - current.lastT <= MAX_GAP_MS;
    if (continues) {
      current.lastT = p.t;
      return;
    }
    if (current) intervals.push(current);
    current = p.fault ? { code: p.code, fromState: p.fromState, start: p.t, lastT: p.t } : null;
  });
  if (current) intervals.push(current);

  return intervals.map(({ code, fromState, start, lastT }) => ({
    code,
    start,
    end: lastT + SNAPSHOT_MS,
    text: textOf(code, fromState),
  }));
};

// Chart.js v2 inline plugin; reads its intervals from
// options.plugins.errorBands = { intervals, fillColor, lineColor, textColor }.
export const errorBandsPlugin = {
  id: "errorBands",
  beforeDatasetsDraw(chart, easing, options) {
    const intervals = options && options.intervals;
    if (!intervals || intervals.length === 0) return;

    const xScale = Object.values(chart.scales).find((s) => s.isHorizontal());
    const area = chart.chartArea;
    if (!xScale || !area) return;

    const { ctx } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.rect(area.left, area.top, area.right - area.left, area.bottom - area.top);
    ctx.clip();
    ctx.font = "12px sans-serif";
    ctx.textBaseline = "top";

    intervals.forEach((band) => {
      const x0 = Math.max(xScale.getPixelForValue(band.start), area.left);
      const x1 = Math.min(xScale.getPixelForValue(band.end), area.right);
      if (!(x1 > area.left && x0 < area.right)) return;
      const width = Math.max(x1 - x0, 1);

      ctx.fillStyle = options.fillColor;
      ctx.fillRect(x0, area.top, width, area.bottom - area.top);
      ctx.fillStyle = options.lineColor;
      ctx.fillRect(x0, area.top, 1, area.bottom - area.top);
      ctx.fillRect(x0 + width - 1, area.top, 1, area.bottom - area.top);

      // Longest label that fits the band; none at all for a sliver.
      const labels = [
        band.text ? `Failure: ${band.code} - ${band.text}` : `Failure: ${band.code}`,
        `${band.code}`,
      ];
      const label = labels.find((l) => ctx.measureText(l).width + 8 <= width);
      if (label) {
        ctx.fillStyle = options.textColor;
        ctx.fillText(label, x0 + 4, area.top + 4);
      }
    });

    ctx.restore();
  },
};
